import { LitElement, html, css, svg } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { store } from '../../store';
import { api } from '../../api';
import { t } from '../../i18n';
import { money, relativeTime } from '../../format';
import { icon, type IconName } from '../../components/ui/icons';
import { showToast } from '../../components/ui/toast';

interface TrendPoint { month: string; active: number }
interface RetentionWindow { window: '3m' | '6m' | '12m'; rate: number | null; activeThen: number; retained: number }
interface ActivityRow { id: string; action: string; entity_type: string | null; entity_id: string | null; actor: string | null; created_at: string }
interface NeedsAttention { overduePayments: number | null; pendingOnboarding: number | null; unconvertedEntries: number | null }
interface UpcomingItem { kind: 'session' | 'event'; id: string; date: string; time: string | null; title: string }
interface DashboardData {
  students: { active: number; trend: TrendPoint[] } | null;
  retention: RetentionWindow[] | null;
  revenue: { primary_currency: string; thisMonth: number; lastMonth: number; sameMonthLastYear: number } | null;
  outstanding: { primary_currency: string; pending: number; overdue: number; total: number } | null;
  expenseBreakdown: { primary_currency: string; rows: { category: string; amount: number }[] } | null;
  recentActivity: ActivityRow[] | null;
  needsAttention: NeedsAttention | null;
  upcoming: UpcomingItem[] | null;
}

/** Maps a curated audit action to its feed icon + i18n label key. */
const ACTIVITY_META: Record<string, { icon: IconName; key: string }> = {
  'student.created': { icon: 'user-plus', key: 'studentCreated' },
  'student.role_added': { icon: 'user-plus', key: 'studentCreated' },
  'student.status_changed': { icon: 'user', key: 'statusChanged' },
  'student.level_changed': { icon: 'graduation-cap', key: 'levelChanged' },
  'payment.recorded': { icon: 'wallet', key: 'paymentRecorded' },
  'credit.issued': { icon: 'wallet', key: 'creditIssued' },
  'ledger.entry_created': { icon: 'receipt', key: 'ledgerEntry' },
  'class.enrolled': { icon: 'users', key: 'enrolled' },
  'event.created': { icon: 'calendar', key: 'eventCreated' },
};

/** Admin home dashboard (Phase 5.5). Six widgets over `/dashboard`; each section is
 *  null when the viewer lacks the permission, so only permitted widgets render.
 *  Teachers get the class-focused `<cadence-teacher-dashboard>` instead. */
@customElement('cadence-dashboard-page')
export class CadenceDashboardPage extends LitElement {
  @state() private loading = true;
  @state() private data: DashboardData | null = null;

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 4px; font-size: 1.6rem; }
    p.sub { margin: 0 0 var(--space-5); color: var(--color-text-secondary); }
    .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: var(--space-4); margin-bottom: var(--space-4); }
    .value { font-size: 1.9rem; font-weight: 700; }
    .value.income { color: var(--color-success); }
    .row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
    .sub { color: var(--color-text-secondary); font-size: 0.78rem; }
    .delta { font-size: 0.82rem; font-weight: 600; display: inline-flex; align-items: center; gap: 3px; }
    .delta svg { width: 14px; height: 14px; }
    .delta.pos { color: var(--color-success); }
    .delta.neg { color: var(--color-danger); }
    .spark { margin-top: 4px; }
    .spark svg { display: block; width: 100%; height: 36px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-4); align-items: start; }
    @media (max-width: 860px) { .grid { grid-template-columns: 1fr; } }
    .stack { display: flex; flex-direction: column; gap: var(--space-4); }
    .retention { display: flex; gap: var(--space-4); flex-wrap: wrap; }
    .retention .r { flex: 1; min-width: 90px; text-align: center; }
    .retention .r .pct { font-size: 1.6rem; font-weight: 700; }
    .retention .r .lbl { color: var(--color-text-secondary); font-size: 0.78rem; margin-top: 2px; }
    .retention .r .meta { color: var(--color-text-secondary); font-size: 0.7rem; }
    .break { display: flex; flex-direction: column; gap: 2px; }
    .break .b { display: flex; justify-content: space-between; gap: 10px; padding: 8px 0; border-bottom: 1px solid var(--color-border); font-size: 0.9rem; }
    .break .b:last-child { border-bottom: none; }
    .feed { display: flex; flex-direction: column; }
    .feed .f { display: flex; align-items: center; gap: 10px; padding: 9px 0; border-bottom: 1px solid var(--color-border); font-size: 0.9rem; color: inherit; text-decoration: none; }
    .feed .f:last-child { border-bottom: none; }
    .feed a.f { cursor: pointer; }
    .feed a.f:hover .txt > div:first-child { color: var(--color-primary); }
    .feed .f .ic { display: inline-flex; color: var(--color-text-secondary); }
    .feed .f .ic svg { width: 16px; height: 16px; }
    .feed .f .txt { flex: 1; min-width: 0; }
    .feed .f .who { color: var(--color-text-secondary); font-size: 0.78rem; }
    .feed .f .when { color: var(--color-text-secondary); font-size: 0.76rem; white-space: nowrap; }
    a.viewmore { color: var(--color-primary); text-decoration: none; font-size: 0.8rem; font-weight: 600; white-space: nowrap; }
    a.viewmore:hover { text-decoration: underline; }
    .actions { display: flex; flex-direction: column; }
    .actions .a { display: flex; align-items: center; gap: 10px; padding: 9px 0; border-bottom: 1px solid var(--color-border); font-size: 0.9rem; color: inherit; text-decoration: none; cursor: pointer; }
    .actions .a:last-child { border-bottom: none; }
    .actions .a .ic { display: inline-flex; color: var(--color-text-secondary); }
    .actions .a .ic svg { width: 16px; height: 16px; }
    .actions .a .lbl { flex: 1; min-width: 0; }
    .actions .a:hover .lbl { color: var(--color-primary); }
    .actions .a .badge { background: var(--color-danger); color: #fff; font-size: 0.75rem; font-weight: 700; border-radius: 999px; padding: 1px 8px; min-width: 20px; text-align: center; }
    .allclear { display: flex; align-items: center; gap: 8px; margin: 4px 0 0; color: var(--color-text-secondary); font-size: 0.9rem; }
    .allclear svg { width: 16px; height: 16px; color: var(--color-success); }
    .muted { color: var(--color-text-secondary); font-size: 0.88rem; }
    .span2 { grid-column: 1 / -1; }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    if (store.get().user?.user_type !== 'teacher') this.load();
  }

  private async load() {
    this.loading = true;
    try {
      this.data = await api.get<DashboardData>('/dashboard');
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      this.loading = false;
    }
  }

  render() {
    const user = store.get().user;
    // Teachers get a class/attendance-focused dashboard.
    if (user?.user_type === 'teacher') return html`<cadence-teacher-dashboard></cadence-teacher-dashboard>`;
    const name = user?.first_name || user?.email || '';
    return html`
      <cadence-break-banner></cadence-break-banner>
      <h1>${t('auth.dashboard.welcome', { name })}</h1>
      <p class="sub">${t('auth.dashboard.subtitle')}</p>
      ${this.loading
        ? html`<cadence-loading></cadence-loading>`
        : this.data
          ? this.renderWidgets(this.data)
          : html`<p class="muted">${t('auth.dashboard.placeholder')}</p>`}
    `;
  }

  private renderWidgets(d: DashboardData) {
    return html`
      <div class="cards">
        ${d.students ? this.studentsCard(d.students) : ''}
        ${d.revenue ? this.revenueCard(d.revenue) : ''}
        ${d.outstanding ? this.outstandingCard(d.outstanding) : ''}
      </div>
      <div class="grid">
        ${d.retention ? this.retentionCard(d.retention) : ''}
        ${d.expenseBreakdown ? this.expenseCard(d.expenseBreakdown) : ''}
        ${d.recentActivity ? this.activityCard(d.recentActivity) : ''}
        ${d.needsAttention || d.upcoming
          ? html`<div class="stack">
              ${d.needsAttention ? this.needsAttentionCard(d.needsAttention) : ''}
              ${d.upcoming ? this.upcomingCard(d.upcoming) : ''}
            </div>`
          : ''}
      </div>
    `;
  }

  // --- KPI cards -----------------------------------------------------------

  private studentsCard(s: { active: number; trend: TrendPoint[] }) {
    return html`
      <cadence-stat-card heading=${t('dashboard.activeStudents')} icon="users">
        <span class="value">${s.active}</span>
        <div class="spark">${this.sparkline(s.trend.map((p) => p.active))}</div>
      </cadence-stat-card>`;
  }

  private revenueCard(r: { primary_currency: string; thisMonth: number; lastMonth: number; sameMonthLastYear: number }) {
    const delta = r.lastMonth > 0 ? Math.round(((r.thisMonth - r.lastMonth) / r.lastMonth) * 100) : null;
    const up = delta != null && delta >= 0;
    return html`
      <cadence-stat-card heading=${t('dashboard.revenueThisMonth')} icon="wallet">
        <div class="row">
          <span class="value income">${money(r.thisMonth, r.primary_currency)}</span>
          ${delta != null
            ? html`<span class="delta ${up ? 'pos' : 'neg'}">${icon(up ? 'trending-up' : 'trending-down')} ${up ? '+' : ''}${delta}%</span>`
            : ''}
        </div>
        <span class="sub">${t('dashboard.vsLastMonth')}: ${money(r.lastMonth, r.primary_currency)}</span>
        <span class="sub">${t('dashboard.vsLastYear')}: ${money(r.sameMonthLastYear, r.primary_currency)}</span>
      </cadence-stat-card>`;
  }

  private outstandingCard(o: { primary_currency: string; pending: number; overdue: number; total: number }) {
    return html`
      <cadence-stat-card heading=${t('dashboard.outstanding')} icon="clock">
        <span class="value">${money(o.total, o.primary_currency)}</span>
        <span class="sub">${t('dashboard.pending')}: ${money(o.pending, o.primary_currency)} · ${t('dashboard.overdue')}: ${money(o.overdue, o.primary_currency)}</span>
      </cadence-stat-card>`;
  }

  // --- Detail cards --------------------------------------------------------

  private retentionCard(rows: RetentionWindow[]) {
    const label: Record<RetentionWindow['window'], string> = {
      '3m': t('dashboard.retention3'), '6m': t('dashboard.retention6'), '12m': t('dashboard.retention12'),
    };
    return html`
      <cadence-stat-card heading=${t('dashboard.retentionRate')} icon="users">
        <div class="retention">
          ${rows.map((r) => html`
            <div class="r">
              <div class="pct">${r.rate == null ? '—' : `${r.rate}%`}</div>
              <div class="lbl">${label[r.window]}</div>
              <div class="meta">${r.retained}/${r.activeThen}</div>
            </div>`)}
        </div>
      </cadence-stat-card>`;
  }

  private expenseCard(e: { primary_currency: string; rows: { category: string; amount: number }[] }) {
    return html`
      <cadence-stat-card heading=${t('dashboard.expenseBreakdown')} icon="receipt">
        ${e.rows.length === 0
          ? html`<p class="muted">${t('dashboard.noExpenses')}</p>`
          : html`<div class="break">
              ${e.rows.map((row) => html`<div class="b"><span>${row.category}</span><span>${money(row.amount, e.primary_currency)}</span></div>`)}
            </div>`}
      </cadence-stat-card>`;
  }

  private activityCard(rows: ActivityRow[]) {
    return html`
      <cadence-stat-card heading=${t('dashboard.recentActivity')} icon="activity">
        ${store.hasPermission('settings.view')
          ? html`<a slot="actions" class="viewmore" data-link href="/admin/audit">${t('dashboard.viewAll')}</a>`
          : ''}
        ${rows.length === 0
          ? html`<p class="muted">${t('dashboard.noActivity')}</p>`
          : html`<div class="feed">
              ${rows.map((r) => {
                const meta = ACTIVITY_META[r.action];
                return html`<div class="f">
                  <span class="ic">${icon(meta?.icon ?? 'activity', 16)}</span>
                  <div class="txt">
                    <div>${meta ? t(`dashboard.activity.${meta.key}`) : r.action}</div>
                    ${r.actor ? html`<div class="who">${r.actor}</div>` : ''}
                  </div>
                  <span class="when">${relativeTime(r.created_at)}</span>
                </div>`;
              })}
            </div>`}
      </cadence-stat-card>`;
  }

  // --- Action panels -------------------------------------------------------

  private needsAttentionCard(n: NeedsAttention) {
    // Only surface rows the viewer can see (non-null) AND that have something to do (> 0).
    const rows: { icon: IconName; label: string; count: number; href: string }[] = [];
    if (n.overduePayments) rows.push({ icon: 'wallet', label: t('dashboard.overduePayments'), count: n.overduePayments, href: '/admin/accounting' });
    if (n.pendingOnboarding) rows.push({ icon: 'user-plus', label: t('dashboard.pendingOnboarding'), count: n.pendingOnboarding, href: '/admin/students' });
    if (n.unconvertedEntries) rows.push({ icon: 'receipt', label: t('dashboard.unconvertedEntries'), count: n.unconvertedEntries, href: '/admin/accounting' });
    return html`
      <cadence-stat-card heading=${t('dashboard.needsAttention')} icon="circle-alert">
        ${rows.length === 0
          ? html`<p class="allclear">${icon('check')} ${t('dashboard.allClear')}</p>`
          : html`<div class="actions">
              ${rows.map((r) => html`
                <a class="a" data-link href=${r.href}>
                  <span class="ic">${icon(r.icon, 16)}</span>
                  <span class="lbl">${r.label}</span>
                  <span class="badge">${r.count}</span>
                </a>`)}
            </div>`}
      </cadence-stat-card>`;
  }

  private upcomingCard(items: UpcomingItem[]) {
    return html`
      <cadence-stat-card heading=${t('dashboard.upcoming')} icon="calendar">
        ${items.length === 0
          ? html`<p class="muted">${t('dashboard.noUpcoming')}</p>`
          : html`<div class="feed">
              ${items.map((it) => html`
                <a class="f" data-link href=${it.kind === 'session' ? `/admin/classes/${it.id}` : `/admin/events/${it.id}`}>
                  <span class="ic">${icon(it.kind === 'session' ? 'music' : 'calendar', 16)}</span>
                  <div class="txt">
                    <div>${it.title}</div>
                    <div class="who">${this.whenLabel(it)}</div>
                  </div>
                </a>`)}
            </div>`}
      </cadence-stat-card>`;
  }

  /** "Mon 14 Jul · 18:00" style label for an upcoming item, in the browser locale. */
  private whenLabel(it: UpcomingItem): string {
    const d = new Date(`${it.date}T00:00:00`);
    const day = d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    return it.time ? `${day} · ${it.time}` : day;
  }

  // --- Sparkline (hand-rolled inline SVG, no chart lib) --------------------

  private sparkline(values: number[]) {
    const w = 200; const h = 36; const pad = 3;
    if (values.length < 2) return html`<span class="muted">—</span>`;
    const min = Math.min(...values); const max = Math.max(...values);
    const span = max - min || 1;
    const pts = values.map((v, i) => {
      const x = pad + (i / (values.length - 1)) * (w - pad * 2);
      const y = h - pad - ((v - min) / span) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    const lastX = w - pad;
    const lastY = h - pad - ((values[values.length - 1] - min) / span) * (h - pad * 2);
    return html`
      <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" aria-hidden="true">
        ${svg`<polyline points=${pts} fill="none" stroke="var(--color-primary)" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />`}
        ${svg`<circle cx=${lastX.toFixed(1)} cy=${lastY.toFixed(1)} r="2.5" fill="var(--color-primary)" />`}
      </svg>`;
  }
}
